import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { toast } from 'sonner';
import { Form, FormActions, FormMessage } from '@/components/form';
import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { usePropertyBasicsMutation } from '@/hooks/useDashboardProperties';
import { propertyFormSchema } from '@/lib/validation/property';
import type { PropertyFormValues } from '@/lib/types/property';
import { PropertyEditBasicsSection } from './PropertyEditBasicsSection';
import { PropertyEditAddressSection } from './PropertyEditAddressSection';
import { PropertyEditHeroMediaSection } from './PropertyEditHeroMediaSection';
import { BOOLEAN_KEYS, buildPropertyEditPayload, createInitialState } from './formState';
import type {
  PropertyEditFieldChangeFactory,
  PropertyEditFormErrors,
  PropertyEditFormState,
} from './types';
import { isPropertyEditFormErrorKey, mapPropertyEditIssues } from './validation';

export interface PropertyEditFormProps {
  propertyId: string;
  property: PropertyFormValues;
  onCancel: () => void;
  onSaved: () => void;
}

export function PropertyEditForm({
  propertyId,
  property,
  onCancel,
  onSaved,
}: PropertyEditFormProps): JSX.Element {
  const user = useDashboardUser();
  const mutation = usePropertyBasicsMutation({ user });
  const [formState, setFormState] = useState<PropertyEditFormState>(() =>
    createInitialState(property),
  );
  const [errors, setErrors] = useState<PropertyEditFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const clearFieldError = useCallback((key: keyof PropertyEditFormState) => {
    if (!isPropertyEditFormErrorKey(key)) {
      return;
    }
    setErrors((current) => {
      if (typeof current[key] !== 'string') {
        return current;
      }
      const { [key]: _removed, ...rest } = current;
      return rest;
    });
  }, []);
  const onFieldChange: PropertyEditFieldChangeFactory = useCallback(
    (key) => (event) => {
      let nextValue: string | boolean;
      if (BOOLEAN_KEYS.has(key)) {
        if (!(event.target instanceof HTMLInputElement)) {
          throw new Error(`Expected checkbox input for ${key}`);
        }
        nextValue = event.target.checked;
      } else {
        nextValue = event.target.value;
      }
      setFormState((current) => ({
        ...current,
        [key]: nextValue,
      }));
      clearFieldError(key);
    },
    [clearFieldError],
  );
  const disabled = mutation.isPending || isUploadingMedia;
  const inputClass = useMemo(
    () =>
      'w-full rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 disabled:cursor-not-allowed disabled:opacity-60 md:text-sm',
    [],
  );
  const handleSubmit = useCallback(() => {
    if (mutation.isPending) {
      return;
    }
    setFormError(null);
    setErrors({});
    if (isUploadingMedia) {
      setFormError('Wait for uploads to finish.');
      return;
    }
    const validation = propertyFormSchema.safeParse(
      buildPropertyEditPayload({ property, formState }),
    );
    if (!validation.success) {
      setErrors(mapPropertyEditIssues(validation.error.issues));
      const firstMessage = validation.error.issues.at(0)?.message ?? 'Fix the highlighted fields.';
      setFormError(firstMessage);
      return;
    }
    mutation.mutate(
      { propertyId, values: validation.data },
      {
        onSuccess: () => {
          toast.success('Property updated.');
          onSaved();
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Unable to update property.';
          setFormError(message);
          toast.error('Unable to update property.', { description: message });
        },
      },
    );
  }, [formState, isUploadingMedia, mutation, onSaved, property, propertyId]);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.key || event.key.toLowerCase() !== 's') {
        return;
      }
      if (event.altKey || !(event.metaKey || event.ctrlKey) || event.repeat) {
        return;
      }
      event.preventDefault();
      handleSubmit();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSubmit]);
  return (
    <Form
      gap="lg"
      onSubmit={(event) => {
        if (!event.currentTarget.reportValidity()) {
          return;
        }
        event.preventDefault();
        handleSubmit();
      }}
    >
      {formError !== null ? (
        <FormMessage tone="error" role="alert">
          {formError}
        </FormMessage>
      ) : null}
      <PropertyEditBasicsSection
        formState={formState}
        errors={errors}
        disabled={disabled}
        inputClass={inputClass}
        onFieldChange={onFieldChange}
      />
      <PropertyEditHeroMediaSection
        propertyId={propertyId}
        heroMediaUrls={formState.heroMediaUrls}
        disabled={disabled}
        error={errors.heroMediaUrls}
        onUploadingChange={setIsUploadingMedia}
        onClearError={() => {
          clearFieldError('heroMediaUrls');
        }}
        onHeroMediaUrlsChange={(updater) => {
          setFormState((current) => ({
            ...current,
            heroMediaUrls: updater(current.heroMediaUrls),
          }));
          clearFieldError('heroMediaUrls');
        }}
      />
      <PropertyEditAddressSection
        formState={formState}
        errors={errors}
        disabled={disabled}
        inputClass={inputClass}
        onFieldChange={onFieldChange}
      />
      <div
        className="sticky bottom-0 z-10 -mx-6 border-t border-surface-subtle bg-white px-6 pt-4"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
        data-testid="dashboard-save-bar"
      >
        <FormActions justify="between">
          <button
            type="button"
            className="rounded-full px-4 py-2 text-sm font-semibold text-text-primary/70 transition hover:bg-surface-subtle disabled:opacity-60"
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </button>
          <PillButton type="submit" disabled={disabled}>
            {disabled ? 'Saving…' : 'Save property'}
          </PillButton>
        </FormActions>
      </div>
    </Form>
  );
}
